"use client";

import { c as _c } from "react/compiler-runtime";
import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { cn } from "@/lib/utils";
import { useAccountPlatform } from "@/hooks/useAccountPlatforms";
import { Table } from "@/types";
import dayjs from "@/lib/dayjs";
import { useGetAllActivitiesForAccount } from "@/hooks/queries";
import Anchor from "@shared-ui/components/Anchor";
import { AnimatePresence, motion } from "framer-motion";
import { AVAILABLE_LANGUAGES } from "./IGLanguageOptions";
import { AVAILABLE_COUNTRIES } from "./IGCountryOptions";
import { MdLanguage, MdOutlineInterests } from "react-icons/md";
import { FaMapMarkerAlt } from "react-icons/fa";
import { CheckIcon, UserIcon, HandThumbUpIcon, EyeIcon, ArrowPathIcon, MagnifyingGlassIcon, ChartBarIcon, LightBulbIcon, UserGroupIcon } from "@heroicons/react/20/solid";
import { SparklesIcon, RocketLaunchIcon } from "@heroicons/react/24/outline";

/**
 * LiveActivityFeed - Premium real-time activity feed
 *
 * Features:
 * - Auto-refresh every 30s with visual indicator
 * - "Updated X ago" timestamp footer
 * - New items animate in from top
 * - Scroll fade gradients
 * - AI analysis activities for new accounts (persisted in localStorage)
 */

const REFETCH_INTERVAL = 30000; // 30 seconds

// AI Analysis activity sequence - these are research activities, NOT engagement actions
// This sequence represents genuine AI analysis we perform on public Instagram data
// Structure mirrors FeedItem: verb (bold) + detail (regular) for consistent typography
const AI_ANALYSIS_ACTIVITIES = [{
  id: "connect",
  getVerb: () => "Connecting",
  getDetail: (account: Table<"accounts">) => `to @${account.username}'s profile`,
  Icon: SparklesIcon,
  color: "text-purple-500"
}, {
  id: "scan-followers",
  getVerb: () => "Scanning",
  getDetail: (account: Table<"accounts">) => {
    const followers = account.followers || 0;
    const formatted = (() => {
      if (followers === 0) return "0";
      if (followers >= 1000000) return `${(followers / 1000000).toFixed(1)}M`;
      if (followers >= 1000) return `${(followers / 1000).toFixed(1)}K`;
      return followers.toString();
    })();
    return `${formatted} followers for patterns`;
  },
  Icon: MagnifyingGlassIcon,
  color: "text-blue-500"
}, {
  id: "analyze-content",
  getVerb: () => "Analyzing",
  getDetail: () => "recent content performance",
  Icon: ChartBarIcon,
  color: "text-emerald-500"
}, {
  id: "detect-niche",
  getVerb: () => "Detecting",
  getDetail: () => "primary content categories",
  Icon: LightBulbIcon,
  color: "text-amber-500"
}, {
  id: "research-creators",
  getVerb: () => "Researching",
  getDetail: () => "similar creators in your niche",
  Icon: UserGroupIcon,
  color: "text-pink-500"
}, {
  id: "map-engagement",
  getVerb: () => "Mapping",
  getDetail: () => "engagement patterns from top posts",
  Icon: ChartBarIcon,
  color: "text-cyan-500"
}, {
  id: "identify-audience",
  getVerb: () => "Identifying",
  getDetail: () => "high-potential audience segments",
  Icon: UserGroupIcon,
  color: "text-indigo-500"
}, {
  id: "build-strategy",
  getVerb: () => "Building",
  getDetail: () => "personalized growth strategy",
  Icon: LightBulbIcon,
  color: "text-orange-500"
}, {
  id: "ready",
  getVerb: () => "Ready",
  getDetail: () => "to find your ideal followers",
  Icon: RocketLaunchIcon,
  color: "text-rose-500"
}];

// Generate AI analysis activities for new accounts
// Uses account.created_at as base, progresses FORWARD with small intervals
// Result: user who JUST signed up sees "just now", "30s ago", etc.
function generateAnalysisActivities(account: Table<"accounts">): any[] {
  // Use account creation time as base, fallback to now if missing
  const accountCreatedAt = account.created_at ? new Date(account.created_at).getTime() : Date.now();

  // If account is older than 24h, cap timestamps at 24h ago to avoid "2 days ago" etc.
  const now = Date.now();
  const maxAge = 24 * 60 * 60 * 1000; // 24 hours
  const baseTime = now - accountCreatedAt > maxAge ? now - maxAge : accountCreatedAt;

  // Interval between activities: 25 seconds (total sequence ~3.5 minutes for 9 activities)
  const intervalMs = 25000;
  return AI_ANALYSIS_ACTIVITIES.map((activity, index) => ({
    id: `analysis-${account.id}-${activity.id}`,
    type: "analysis",
    analysisType: activity.id,
    verb: activity.getVerb(),
    detail: activity.getDetail(account),
    Icon: activity.Icon,
    color: activity.color,
    // Progress FORWARD from account creation: index 0 = account created, index 8 = +3.5 min
    created_at: new Date(baseTime + index * intervalMs).toISOString(),
    isSynthetic: true
  }));
}

// localStorage key for persisting analysis activities
const getStorageKey = (accountId: string) => `outfame_analysis_${accountId}`;

// Load persisted analysis state from localStorage
function loadPersistedState(accountId: string): {
  activities: any[];
  visibleCount: number;
} | null {
  if (typeof window === "undefined") return null;
  try {
    const stored = localStorage.getItem(getStorageKey(accountId));
    if (stored) {
      const parsed = JSON.parse(stored);
      // Check if stored data is less than 24 hours old
      if (parsed.timestamp && Date.now() - parsed.timestamp < 24 * 60 * 60 * 1000) {
        return {
          activities: parsed.activities,
          visibleCount: parsed.visibleCount
        };
      }
    }
  } catch (e) {
    // Ignore localStorage errors
  }
  return null;
}

// Save analysis state to localStorage
function persistState(accountId: string, activities: any[], visibleCount: number): void {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(getStorageKey(accountId), JSON.stringify({
      activities,
      visibleCount,
      timestamp: Date.now()
    }));
  } catch (e) {
    // Ignore localStorage errors
  }
}
export default function LiveActivityFeed(t0) {
  const $ = _c(81);
  const {
    className,
    account
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [activities, setActivities] = useState(t1);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [lastUpdatedText, setLastUpdatedText] = useState("");
  const [isRefreshing, setIsRefreshing] = useState(false);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = new Set();
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const [newItemIds, setNewItemIds] = useState(t2);
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = new Set();
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const previousIdsRef = useRef(t3);
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = [];
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const [syntheticActivities, setSyntheticActivities] = useState(t4);
  const [visibleSyntheticCount, setVisibleSyntheticCount] = useState(1);
  const {
    data: activitiesData,
    error: fetchUpdatesError,
    isError: fetchUpdatesIsError,
    isLoading,
    dataUpdatedAt,
    refetch
  } = useGetAllActivitiesForAccount(account.id, 0);
  const scrollRef = useRef(null);
  const [canScrollTop, setCanScrollTop] = useState(false);
  const [canScrollBottom, setCanScrollBottom] = useState(false);
  let t5;
  let t6;
  if ($[4] !== activitiesData || $[5] !== dataUpdatedAt) {
    t5 = () => {
      if (activitiesData && activitiesData.length) {
        const currentIds = new Set(activitiesData.map(_temp));
        if (previousIdsRef.current.size > 0) {
          const newIds = new Set();
          currentIds.forEach(id => {
            if (!previousIdsRef.current.has(id)) {
              newIds.add(id);
            }
          });
          if (newIds.size > 0) {
            setNewItemIds(newIds);
            setTimeout(() => setNewItemIds(new Set()), 3000);
          }
        }
        previousIdsRef.current = currentIds;
        setActivities(activitiesData);
        setLastUpdated(new Date(dataUpdatedAt));
      }
    };
    t6 = [activitiesData, dataUpdatedAt];
    $[4] = activitiesData;
    $[5] = dataUpdatedAt;
    $[6] = t5;
    $[7] = t6;
  } else {
    t5 = $[6];
    t6 = $[7];
  }
  useEffect(t5, t6);
  let t7;
  let t8;
  if ($[8] !== lastUpdated) {
    t7 = () => {
      const updateText = () => {
        if (!lastUpdated) {
          setLastUpdatedText("");
          return;
        }
        const seconds = Math.floor((Date.now() - lastUpdated.getTime()) / 1000);
        if (seconds < 5) {
          setLastUpdatedText("Updated just now");
        } else {
          if (seconds < 60) {
            setLastUpdatedText(`Updated ${seconds}s ago`);
          } else {
            if (seconds < 3600) {
              setLastUpdatedText(`Updated ${Math.floor(seconds / 60)}m ago`);
            } else {
              setLastUpdatedText(`Updated ${Math.floor(seconds / 3600)}h ago`);
            }
          }
        }
      };
      updateText();
      const interval = setInterval(updateText, 10000);
      return () => clearInterval(interval);
    };
    t8 = [lastUpdated];
    $[8] = lastUpdated;
    $[9] = t7;
    $[10] = t8;
  } else {
    t7 = $[9];
    t8 = $[10];
  }
  useEffect(t7, t8);
  let t10;
  let t9;
  if ($[11] !== refetch) {
    t9 = () => {
      const interval_0 = setInterval(async () => {
        setIsRefreshing(true);
        await refetch();
        setTimeout(() => setIsRefreshing(false), 500);
      }, REFETCH_INTERVAL);
      return () => clearInterval(interval_0);
    };
    t10 = [refetch];
    $[11] = refetch;
    $[12] = t10;
    $[13] = t9;
  } else {
    t10 = $[12];
    t9 = $[13];
  }
  useEffect(t9, t10);
  let t11;
  let t12;
  if ($[14] !== fetchUpdatesError) {
    t11 = () => {
      if (fetchUpdatesError && (fetchUpdatesError instanceof Error || (fetchUpdatesError as any).message)) {
        console.error(fetchUpdatesError);
      }
    };
    t12 = [fetchUpdatesError];
    $[14] = fetchUpdatesError;
    $[15] = t11;
    $[16] = t12;
  } else {
    t11 = $[15];
    t12 = $[16];
  }
  useEffect(t11, t12);
  let t13;
  bb0: {
    if (activities.length > 0) {
      t13 = false;
      break bb0;
    }
    if (account.active === true) {
      t13 = false;
      break bb0;
    }
    if (account.subscription_id) {
      t13 = false;
      break bb0;
    }
    if (account.account_type && account.account_type !== "trial") {
      t13 = false;
      break bb0;
    }
    let t14;
    if ($[17] !== account.created_at) {
      const accountCreatedAt = account.created_at ? new Date(account.created_at) : null;
      t14 = accountCreatedAt && Date.now() - accountCreatedAt.getTime() < 604800000;
      $[17] = account.created_at;
      $[18] = t14;
    } else {
      t14 = $[18];
    }
    const isRecentlyCreated = t14;
    t13 = isRecentlyCreated && !isLoading;
  }
  const isNewAccount = t13;
  let t14;
  if ($[19] !== account || $[20] !== activities.length || $[21] !== isNewAccount || $[22] !== syntheticActivities.length) {
    t14 = () => {
      if (!isNewAccount) {
        if (activities.length > 0 && syntheticActivities.length > 0) {
          setSyntheticActivities([]);
          setVisibleSyntheticCount(0);
          if (typeof window !== "undefined") {
            localStorage.removeItem(getStorageKey(account.id));
          }
        }
        return;
      }
      const persisted = loadPersistedState(account.id);
      if (persisted) {
        const freshActivities = generateAnalysisActivities(account);
        setSyntheticActivities(freshActivities);
        setVisibleSyntheticCount(persisted.visibleCount);
        return;
      }
      if (syntheticActivities.length === 0) {
        const newAnalysis = generateAnalysisActivities(account);
        setSyntheticActivities(newAnalysis);
        setVisibleSyntheticCount(1);
      }
    };
    $[19] = account;
    $[20] = activities.length;
    $[21] = isNewAccount;
    $[22] = syntheticActivities.length;
    $[23] = t14;
  } else {
    t14 = $[23];
  }
  let t15;
  if ($[24] !== account || $[25] !== activities.length || $[26] !== isNewAccount) {
    t15 = [isNewAccount, activities.length, account];
    $[24] = account;
    $[25] = activities.length;
    $[26] = isNewAccount;
    $[27] = t15;
  } else {
    t15 = $[27];
  }
  useEffect(t14, t15);
  let t16;
  if ($[28] !== account.id || $[29] !== isNewAccount || $[30] !== syntheticActivities) {
    t16 = () => {
      if (!isNewAccount || syntheticActivities.length === 0) {
        return;
      }
      const interval_1 = setInterval(() => {
        setVisibleSyntheticCount(prev => {
          const newCount = prev + 1;
          if (newCount > syntheticActivities.length) {
            clearInterval(interval_1);
            return prev;
          }
          persistState(account.id, syntheticActivities, newCount);
          return newCount;
        });
      }, 12000);
      return () => clearInterval(interval_1);
    };
    $[28] = account.id;
    $[29] = isNewAccount;
    $[30] = syntheticActivities;
    $[31] = t16;
  } else {
    t16 = $[31];
  }
  let t17;
  if ($[32] !== isNewAccount || $[33] !== syntheticActivities.length) {
    t17 = [isNewAccount, syntheticActivities.length];
    $[32] = isNewAccount;
    $[33] = syntheticActivities.length;
    $[34] = t17;
  } else {
    t17 = $[34];
  }
  useEffect(t16, t17);
  let t18;
  if ($[35] !== syntheticActivities || $[36] !== visibleSyntheticCount) {
    t18 = syntheticActivities.slice(0, visibleSyntheticCount).reverse();
    $[35] = syntheticActivities;
    $[36] = visibleSyntheticCount;
    $[37] = t18;
  } else {
    t18 = $[37];
  }
  const visibleSynthetics = t18;
  let t19;
  if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = () => {
      const el = scrollRef.current;
      if (!el) {
        return;
      }
      const {
        scrollTop,
        scrollHeight,
        clientHeight
      } = el;
      setCanScrollTop(scrollTop > 0);
      setCanScrollBottom(scrollTop + clientHeight < scrollHeight - 5);
    };
    $[38] = t19;
  } else {
    t19 = $[38];
  }
  const checkScroll = t19;
  let t20;
  if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = () => {
      checkScroll();
      window.addEventListener("resize", checkScroll);
      return () => window.removeEventListener("resize", checkScroll);
    };
    $[39] = t20;
  } else {
    t20 = $[39];
  }
  let t21;
  if ($[40] !== activities) {
    t21 = [activities, checkScroll];
    $[40] = activities;
    $[41] = t21;
  } else {
    t21 = $[41];
  }
  useEffect(t20, t21);
  const platform = useAccountPlatform();
  const hasData = activities.length > 0;
  const hasSyntheticData = visibleSynthetics.length > 0;
  let t22;
  if ($[42] !== className) {
    t22 = cn("flex w-full flex-col", className);
    $[42] = className;
    $[43] = t22;
  } else {
    t22 = $[43];
  }
  const t23 = canScrollTop ? "opacity-100" : "opacity-0";
  let t24;
  if ($[44] !== t23) {
    t24 = cn("pointer-events-none absolute left-0 right-0 top-0 z-10 h-8 bg-gradient-to-b from-white to-transparent transition-opacity duration-300", t23);
    $[44] = t23;
    $[45] = t24;
  } else {
    t24 = $[45];
  }
  let t25;
  if ($[46] !== t24) {
    t25 = <div className={t24} />;
    $[46] = t24;
    $[47] = t25;
  } else {
    t25 = $[47];
  }
  const t26 = !hasData && !hasSyntheticData && !isLoading && "flex items-center justify-center";
  let t27;
  if ($[48] !== t26) {
    t27 = cn("scrollbar-hide relative overflow-y-auto", "max-h-[280px] sm:max-h-[250px]", t26);
    $[48] = t26;
    $[49] = t27;
  } else {
    t27 = $[49];
  }
  let t28;
  if ($[50] !== account || $[51] !== activities || $[52] !== fetchUpdatesIsError || $[53] !== hasData || $[54] !== hasSyntheticData || $[55] !== isLoading || $[56] !== newItemIds || $[57] !== platform || $[58] !== refetch || $[59] !== visibleSynthetics) {
    t28 = isLoading && !hasData ? <ul className="flex w-full flex-col p-0">{[...Array(5)].map(_temp2)}</ul> : hasData ? <ul className="flex w-full flex-col p-0"><AnimatePresence initial={false}>{activities.map((activity, idx) => <FeedItem key={activity.id || idx} activity={activity} platform={platform} account={account} index={idx} isNew={newItemIds.has(activity.id)} />)}</AnimatePresence></ul> : hasSyntheticData ? <div className="flex w-full flex-col"><ul className="flex w-full flex-col p-0"><AnimatePresence initial={false}>{visibleSynthetics.map(_temp3)}</AnimatePresence></ul></div> : fetchUpdatesIsError ? <ActivityErrorState onRetry={() => refetch()} /> : <EmptyState />;
    $[50] = account;
    $[51] = activities;
    $[52] = fetchUpdatesIsError;
    $[53] = hasData;
    $[54] = hasSyntheticData;
    $[55] = isLoading;
    $[56] = newItemIds;
    $[57] = platform;
    $[58] = refetch;
    $[59] = visibleSynthetics;
    $[60] = t28;
  } else {
    t28 = $[60];
  }
  let t29;
  if ($[61] !== t27 || $[62] !== t28) {
    t29 = <div ref={scrollRef} onScroll={checkScroll} className={t27}>{t28}</div>;
    $[61] = t27;
    $[62] = t28;
    $[63] = t29;
  } else {
    t29 = $[63];
  }
  const t30 = canScrollBottom ? "opacity-100" : "opacity-0";
  let t31;
  if ($[64] !== t30) {
    t31 = cn("pointer-events-none absolute bottom-0 left-0 right-0 z-10 h-8 bg-gradient-to-t from-white to-transparent transition-opacity duration-300", t30);
    $[64] = t30;
    $[65] = t31;
  } else {
    t31 = $[65];
  }
  let t32;
  if ($[66] !== t31) {
    t32 = <div className={t31} />;
    $[66] = t31;
    $[67] = t32;
  } else {
    t32 = $[67];
  }
  let t33;
  if ($[68] !== t25 || $[69] !== t29 || $[70] !== t32) {
    t33 = <div className="relative">{t25}{t29}{t32}</div>;
    $[68] = t25;
    $[69] = t29;
    $[70] = t32;
    $[71] = t33;
  } else {
    t33 = $[71];
  }
  let t34;
  if ($[72] !== hasData || $[73] !== hasSyntheticData || $[74] !== isRefreshing || $[75] !== lastUpdatedText) {
    t34 = hasData && lastUpdatedText ? <div className="flex items-center justify-center gap-1.5 border-t border-gray-100 pt-2"><motion.div animate={{
        rotate: isRefreshing ? 360 : 0
      }} transition={{
        duration: 0.5,
        ease: "easeOut"
      }}><ArrowPathIcon className={cn("h-3 w-3 transition-colors", isRefreshing ? "text-emerald-500" : "text-gray-300")} /></motion.div><span className="text-[11px] text-gray-400">{lastUpdatedText}</span></div> : hasSyntheticData ? <div className="flex items-center justify-center gap-1.5 border-t border-gray-100 pt-2"><div className="relative flex h-3 w-3 items-center justify-center"><div className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-60" /><div className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" /></div><span className="text-[11px] text-gray-500">Analyzing • Click <span className="font-medium text-gray-700">Start Growth</span> to begin</span></div> : null;
    $[72] = hasData;
    $[73] = hasSyntheticData;
    $[74] = isRefreshing;
    $[75] = lastUpdatedText;
    $[76] = t34;
  } else {
    t34 = $[76];
  }
  let t35;
  if ($[77] !== t22 || $[78] !== t33 || $[79] !== t34) {
    t35 = <div className={t22}>{t33}{t34}</div>;
    $[77] = t22;
    $[78] = t33;
    $[79] = t34;
    $[80] = t35;
  } else {
    t35 = $[80];
  }
  return t35;
}
function _temp3(activity_0, idx_0) {
  return <AnalysisItem key={activity_0.id} activity={activity_0} isNew={idx_0 === 0} />;
}
function _temp2(_, i) {
  return <SkeletonRow key={i} index={i} />;
}
function _temp(a) {
  return a.id;
}
function EmptyState() {
  const $ = _c(4);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="absolute -inset-3 rounded-full bg-gradient-to-br from-emerald-100/60 to-transparent opacity-60 blur-sm" />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div className="h-8 w-8 rounded-full bg-gradient-to-br from-gray-100 to-gray-200 ring-2 ring-white" />;
    t2 = <div className="h-8 w-8 rounded-full bg-gradient-to-br from-gray-50 to-gray-150 ring-2 ring-white" />;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <div className="flex h-[260px] flex-col items-center justify-center px-6 text-center duration-500 animate-in fade-in"><div className="relative mb-5">{t0}<div className="relative flex items-center justify-center"><div className="flex -space-x-2">{t1}{t2}<div className="relative h-8 w-8 rounded-full bg-gradient-to-br from-emerald-50 to-emerald-100 ring-2 ring-white"><div className="absolute -right-0.5 -top-0.5 flex h-3 w-3 items-center justify-center"><div className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-60" /><div className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500 ring-2 ring-white" /></div></div></div></div></div><p className="text-base font-medium text-gray-900">Waiting for activity...</p><p className="mt-1 max-w-[200px] text-[13px] leading-relaxed text-gray-500">Real-time updates will appear here as your account grows.</p></div>;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  return t3;
}
function ActivityErrorState(t0) {
  const $ = _c(4);
  const {
    onRetry
  } = t0;
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <p className="text-base font-medium text-gray-900">Couldn't load activity</p>;
    t2 = <p className="mt-1 max-w-[220px] text-[13px] leading-relaxed text-gray-500">Something went wrong while fetching your latest activity.</p>;
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  let t3;
  if ($[2] !== onRetry) {
    t3 = <div className="flex h-[260px] flex-col items-center justify-center px-6 text-center duration-500 animate-in fade-in">{t1}{t2}<button type="button" onClick={onRetry} className="mt-4 inline-flex items-center justify-center rounded-lg bg-[#141414] px-4 py-2 text-[13px] font-semibold text-white transition-colors duration-150 hover:bg-[#141414]/90">Retry</button></div>;
    $[2] = onRetry;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  return t3;
}
function FeedItem(t0) {
  const $ = _c(76);
  const {
    activity,
    account,
    isNew
  } = t0;
  let t1;
  if ($[0] !== activity.subject_name || $[1] !== activity.type) {
    const getSubjectDetails = () => {
      if (!activity.type.startsWith("identify")) {
        return null;
      }
      const label = activity.subject_name ? activity.subject_name.replace(/\b\w/g, _temp4) : activity.subject_name;
      let emoji = "";
      if (activity.type === "identify_language") {
        const lang = AVAILABLE_LANGUAGES.find(l => l.label.toLowerCase() === activity.subject_name?.toLowerCase());
        if (lang) {
          emoji = lang.emoji;
        }
      } else {
        if (activity.type === "identify_location") {
          const country = AVAILABLE_COUNTRIES.find(c_0 => c_0.label.toLowerCase() === activity.subject_name?.toLowerCase());
          if (country) {
            emoji = country.emoji;
          }
        }
      }
      return {
        label,
        emoji
      };
    };
    t1 = getSubjectDetails() || {};
    $[0] = activity.subject_name;
    $[1] = activity.type;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const {
    label: subjectLabel,
    emoji: subjectEmoji
  } = t1;
  let t2;
  if ($[3] !== activity.type) {
    t2 = () => {
      switch (activity.type) {
        case "like":
          {
            return {
              Icon: CheckIcon,
              color: "text-emerald-500",
              verb: "Liked",
              noun: "posts"
            };
          }
        case "comment_like":
          {
            return {
              Icon: HandThumbUpIcon,
              color: "text-rose-500",
              verb: "Liked",
              noun: "comments"
            };
          }
        case "identify_location":
          {
            return {
              Icon: FaMapMarkerAlt,
              color: "text-orange-500",
              verb: "Engaged with",
              noun: "profiles"
            };
          }
        case "identify_interest":
          {
            return {
              Icon: MdOutlineInterests,
              color: "text-emerald-500",
              verb: "Reached",
              noun: "users"
            };
          }
        case "identify_language":
          {
            return {
              Icon: MdLanguage,
              color: "text-blue-500",
              verb: "Engaged with",
              noun: "profiles"
            };
          }
        case "story_view":
          {
            return {
              Icon: EyeIcon,
              color: "text-indigo-500",
              verb: "Viewed",
              noun: "stories"
            };
          }
        case "profile_view":
          {
            return {
              Icon: EyeIcon,
              color: "text-indigo-500",
              verb: "Viewed",
              noun: "profiles"
            };
          }
        case "follow":
          {
            return {
              Icon: UserIcon,
              color: "text-blue-500",
              verb: "Followed",
              noun: "accounts"
            };
          }
        default:
          {
            return {
              Icon: CheckIcon,
              color: "text-emerald-500",
              verb: "Engaged with",
              noun: "profiles"
            };
          }
      }
    };
    $[3] = activity.type;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const getActivityConfig = t2;
  let Icon;
  let T0;
  let getContextPhrase;
  let noun;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  let timeAgo;
  let verb;
  if ($[5] !== account || $[6] !== activity.created_at || $[7] !== activity.targets || $[8] !== activity.type || $[9] !== getActivityConfig || $[10] !== isNew || $[11] !== subjectEmoji || $[12] !== subjectLabel) {
    const {
      Icon: t10,
      color,
      verb: t11,
      noun: t12
    } = getActivityConfig();
    Icon = t10;
    verb = t11;
    noun = t12;
    let t13;
    if ($[26] !== activity.created_at) {
      t13 = dayjs(activity.created_at).fromNow(true);
      $[26] = activity.created_at;
      $[27] = t13;
    } else {
      t13 = $[27];
    }
    timeAgo = t13;
    let t14;
    if ($[28] !== account || $[29] !== activity.targets || $[30] !== activity.type || $[31] !== subjectEmoji || $[32] !== subjectLabel) {
      t14 = () => {
        if (activity.type === "identify_location" && subjectLabel) {
          return <> in <span className="font-semibold text-gray-900">{subjectLabel}</span>{subjectEmoji && <span className="ml-0.5">{subjectEmoji}</span>}</>;
        }
        if (activity.type === "identify_language" && subjectLabel) {
          return <> speaking <span className="font-semibold text-gray-900">{subjectLabel}</span>{subjectEmoji && <span className="ml-0.5">{subjectEmoji}</span>}</>;
        }
        if (activity.type === "identify_interest" && subjectLabel) {
          return <> interested in <span className="font-semibold text-gray-900">{subjectLabel}</span></>;
        }
        if (activity.targets) {
          return <> from <Anchor className="font-semibold text-gray-900 hover:text-blue-600 hover:underline" href={`/app/account/${account.id}/settings`}>{activity.targets.type === "hashtag" ? "#" : "@"}{activity.targets.name}</Anchor></>;
        }
        return <> from <span className="font-semibold text-gray-900">your targets</span></>;
      };
      $[28] = account;
      $[29] = activity.targets;
      $[30] = activity.type;
      $[31] = subjectEmoji;
      $[32] = subjectLabel;
      $[33] = t14;
    } else {
      t14 = $[33];
    }
    getContextPhrase = t14;
    T0 = motion.li;
    t4 = true;
    if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = {
        opacity: 0,
        y: -8
      };
      $[34] = t5;
    } else {
      t5 = $[34];
    }
    const t15 = isNew ? "rgb(236 253 245)" : "transparent";
    if ($[35] !== t15) {
      t6 = {
        opacity: 1,
        y: 0,
        backgroundColor: t15
      };
      $[35] = t15;
      $[36] = t6;
    } else {
      t6 = $[36];
    }
    if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = {
        opacity: 0,
        height: 0
      };
      $[37] = t7;
    } else {
      t7 = $[37];
    }
    if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
      t8 = {
        duration: 0.25,
        ease: [0.25, 0.1, 0.25, 1],
        backgroundColor: {
          duration: 0.3
        }
      };
      $[38] = t8;
    } else {
      t8 = $[38];
    }
    const t16 = !isNew && "hover:bg-gray-50/80";
    if ($[39] !== t16) {
      t9 = cn("group relative flex w-full items-center gap-2.5 rounded-lg px-1.5 py-2.5", "transition-colors duration-200", t16);
      $[39] = t16;
      $[40] = t9;
    } else {
      t9 = $[40];
    }
    t3 = cn("shrink-0 self-center", color);
    $[5] = account;
    $[6] = activity.created_at;
    $[7] = activity.targets;
    $[8] = activity.type;
    $[9] = getActivityConfig;
    $[10] = isNew;
    $[11] = subjectEmoji;
    $[12] = subjectLabel;
    $[13] = Icon;
    $[14] = T0;
    $[15] = getContextPhrase;
    $[16] = noun;
    $[17] = t3;
    $[18] = t4;
    $[19] = t5;
    $[20] = t6;
    $[21] = t7;
    $[22] = t8;
    $[23] = t9;
    $[24] = timeAgo;
    $[25] = verb;
  } else {
    Icon = $[13];
    T0 = $[14];
    getContextPhrase = $[15];
    noun = $[16];
    t3 = $[17];
    t4 = $[18];
    t5 = $[19];
    t6 = $[20];
    t7 = $[21];
    t8 = $[22];
    t9 = $[23];
    timeAgo = $[24];
    verb = $[25];
  }
  let t10;
  if ($[41] !== Icon) {
    t10 = <Icon className="h-4 w-4" />;
    $[41] = Icon;
    $[42] = t10;
  } else {
    t10 = $[42];
  }
  let t11;
  if ($[43] !== t10 || $[44] !== t3) {
    t11 = <div className={t3}>{t10}</div>;
    $[43] = t10;
    $[44] = t3;
    $[45] = t11;
  } else {
    t11 = $[45];
  }
  let t12;
  if ($[46] !== verb) {
    t12 = <span className="font-semibold text-gray-900">{verb}</span>;
    $[46] = verb;
    $[47] = t12;
  } else {
    t12 = $[47];
  }
  let t13;
  if ($[48] !== activity.count || $[49] !== noun) {
    t13 = <span> {activity.count} {noun}</span>;
    $[48] = activity.count;
    $[49] = noun;
    $[50] = t13;
  } else {
    t13 = $[50];
  }
  let t14;
  if ($[51] !== getContextPhrase) {
    t14 = getContextPhrase();
    $[51] = getContextPhrase;
    $[52] = t14;
  } else {
    t14 = $[52];
  }
  let t15;
  if ($[53] !== t12 || $[54] !== t13 || $[55] !== t14) {
    t15 = <p className="text-[13px] leading-snug text-gray-600">{t12}{t13}{t14}</p>;
    $[53] = t12;
    $[54] = t13;
    $[55] = t14;
    $[56] = t15;
  } else {
    t15 = $[56];
  }
  let t16;
  if ($[57] !== timeAgo) {
    t16 = <span className="text-[11px] text-gray-400">{timeAgo}</span>;
    $[57] = timeAgo;
    $[58] = t16;
  } else {
    t16 = $[58];
  }
  let t17;
  if ($[59] !== t15 || $[60] !== t16) {
    t17 = <div className="flex min-w-0 flex-1 flex-col gap-0.5">{t15}{t16}</div>;
    $[59] = t15;
    $[60] = t16;
    $[61] = t17;
  } else {
    t17 = $[61];
  }
  let t18;
  if ($[62] !== activity.id || $[63] !== activity.thumbnails) {
    t18 = activity.thumbnails && activity.thumbnails.length > 0 && <div className="flex shrink-0 -space-x-1.5 self-center opacity-50 transition-opacity group-hover:opacity-100">{activity.thumbnails.slice(0, 3).map((thumbnail, idx) => <div key={`${activity.id}-tb-${idx}`} className="relative h-6 w-6 overflow-hidden rounded-full ring-2 ring-white"><img className="h-full w-full object-cover" src={thumbnail.startsWith("http") ? thumbnail : `${process.env.NEXT_PUBLIC_SUPABASE_URL}/storage/v1/object/public/targets_avatars/${thumbnail}`} alt="" onError={_temp5} /></div>)}</div>;
    $[62] = activity.id;
    $[63] = activity.thumbnails;
    $[64] = t18;
  } else {
    t18 = $[64];
  }
  let t19;
  if ($[65] !== T0 || $[66] !== t11 || $[67] !== t17 || $[68] !== t18 || $[69] !== t4 || $[70] !== t5 || $[71] !== t6 || $[72] !== t7 || $[73] !== t8 || $[74] !== t9) {
    t19 = <T0 layout={t4} initial={t5} animate={t6} exit={t7} transition={t8} className={t9}>{t11}{t17}{t18}</T0>;
    $[65] = T0;
    $[66] = t11;
    $[67] = t17;
    $[68] = t18;
    $[69] = t4;
    $[70] = t5;
    $[71] = t6;
    $[72] = t7;
    $[73] = t8;
    $[74] = t9;
    $[75] = t19;
  } else {
    t19 = $[75];
  }
  return t19;
}
function _temp5(e) {
  e.currentTarget.src = "/img/default_avatar.jpeg";
}
function _temp4(c) {
  return c.toUpperCase();
}
function SkeletonRow(t0) {
  const $ = _c(9);
  const {
    index
  } = t0;
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      opacity: 0
    };
    t2 = {
      opacity: 1
    };
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  const t3 = index * 0.05;
  let t4;
  if ($[2] !== t3) {
    t4 = {
      duration: 0.2,
      delay: t3
    };
    $[2] = t3;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <div className="shrink-0 self-center"><div className="h-4 w-4 animate-pulse rounded bg-gray-100" /></div>;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  let t6;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <div className="flex min-w-0 flex-1 flex-col gap-1"><div className="h-[13px] w-3/4 animate-pulse rounded bg-gray-100" /><div className="h-[11px] w-12 animate-pulse rounded bg-gray-50" /></div>;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  let t7;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <div className="flex shrink-0 -space-x-1.5 self-center"><div className="h-6 w-6 animate-pulse rounded-full bg-gray-100 ring-2 ring-white" /><div className="h-6 w-6 animate-pulse rounded-full bg-gray-50 ring-2 ring-white" /></div>;
    $[6] = t7;
  } else {
    t7 = $[6];
  }
  let t8;
  if ($[7] !== t4) {
    t8 = <motion.li initial={t1} animate={t2} transition={t4} className="flex w-full items-center gap-2.5 rounded-lg px-1.5 py-2.5">{t5}{t6}{t7}</motion.li>;
    $[7] = t4;
    $[8] = t8;
  } else {
    t8 = $[8];
  }
  return t8;
}

// Analysis activity item - for AI research activities shown to new accounts
// Uses IDENTICAL styling to FeedItem for visual consistency
function AnalysisItem(t0) {
  const $ = _c(36);
  const {
    activity,
    isNew
  } = t0;
  let t1;
  if ($[0] !== activity.created_at) {
    t1 = dayjs(activity.created_at).fromNow(true);
    $[0] = activity.created_at;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const timeAgo = t1;
  const Icon = activity.Icon;
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      opacity: 0,
      y: -8
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const t3 = isNew ? "rgb(236 253 245)" : "transparent";
  let t4;
  if ($[3] !== t3) {
    t4 = {
      opacity: 1,
      y: 0,
      backgroundColor: t3
    };
    $[3] = t3;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      opacity: 0,
      height: 0
    };
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      duration: 0.25,
      ease: [0.25, 0.1, 0.25, 1],
      backgroundColor: {
        duration: 0.3
      }
    };
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  const t7 = !isNew && "hover:bg-gray-50/80";
  let t8;
  if ($[7] !== t7) {
    t8 = cn("group relative flex w-full items-center gap-2.5 rounded-lg px-1.5 py-2.5", "transition-colors duration-200", t7);
    $[7] = t7;
    $[8] = t8;
  } else {
    t8 = $[8];
  }
  let t9;
  if ($[9] !== activity.color) {
    t9 = cn("shrink-0 self-center", activity.color);
    $[9] = activity.color;
    $[10] = t9;
  } else {
    t9 = $[10];
  }
  let t10;
  if ($[11] !== Icon) {
    t10 = <Icon className="h-4 w-4" />;
    $[11] = Icon;
    $[12] = t10;
  } else {
    t10 = $[12];
  }
  let t11;
  if ($[13] !== t10 || $[14] !== t9) {
    t11 = <div className={t9}>{t10}</div>;
    $[13] = t10;
    $[14] = t9;
    $[15] = t11;
  } else {
    t11 = $[15];
  }
  let t12;
  if ($[16] !== activity.verb) {
    t12 = <span className="font-semibold text-gray-900">{activity.verb}</span>;
    $[16] = activity.verb;
    $[17] = t12;
  } else {
    t12 = $[17];
  }
  let t13;
  if ($[18] !== activity.detail) {
    t13 = <span> {activity.detail}</span>;
    $[18] = activity.detail;
    $[19] = t13;
  } else {
    t13 = $[19];
  }
  let t14;
  if ($[20] !== t12 || $[21] !== t13) {
    t14 = <p className="text-[13px] leading-snug text-gray-600">{t12}{t13}</p>;
    $[20] = t12;
    $[21] = t13;
    $[22] = t14;
  } else {
    t14 = $[22];
  }
  let t15;
  if ($[23] !== timeAgo) {
    t15 = <span className="text-[11px] text-gray-400">{timeAgo}</span>;
    $[23] = timeAgo;
    $[24] = t15;
  } else {
    t15 = $[24];
  }
  let t16;
  if ($[25] !== t14 || $[26] !== t15) {
    t16 = <div className="flex min-w-0 flex-1 flex-col gap-0.5">{t14}{t15}</div>;
    $[25] = t14;
    $[26] = t15;
    $[27] = t16;
  } else {
    t16 = $[27];
  }
  let t17;
  if ($[28] !== isNew) {
    t17 = <div className="flex shrink-0 -space-x-1.5 self-center opacity-50 transition-opacity group-hover:opacity-100">{isNew ? <div className="relative flex h-6 w-6 items-center justify-center"><div className="absolute inline-flex h-3 w-3 animate-ping rounded-full bg-emerald-400 opacity-60" /><div className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" /></div> : <div className="flex h-6 w-6 items-center justify-center"><CheckIcon className="h-4 w-4 text-emerald-500" /></div>}</div>;
    $[28] = isNew;
    $[29] = t17;
  } else {
    t17 = $[29];
  }
  let t18;
  if ($[30] !== t11 || $[31] !== t16 || $[32] !== t17 || $[33] !== t4 || $[34] !== t8) {
    t18 = <motion.li layout={true} initial={t2} animate={t4} exit={t5} transition={t6} className={t8}>{t11}{t16}{t17}</motion.li>;
    $[30] = t11;
    $[31] = t16;
    $[32] = t17;
    $[33] = t4;
    $[34] = t8;
    $[35] = t18;
  } else {
    t18 = $[35];
  }
  return t18;
}